/**
 * AssistantMessage — one question and its answer (mission 8 WP08 T041; FR-008, FR-009, FR-011,
 * FR-012; SAT.2, SAT.7, ANA.6; SC-008; research §1.9, §1.10; style guide §7.12; post-tasks TR-4).
 *
 * - **The question.** A user bubble (`accent-50`, right-aligned, radius 12/12/4/12) named "You" for
 *   screen readers. The question is the stored, masked text: a mask token renders as the text it is.
 *   When `masked_count > 0` the masking notice sits beside it (FR-007).
 * - **The answer.** Plain text with a 1 px `navy-200` left rule, no bubble, in the region
 *   `data-assistant-answer` (WP13's short-value scan region, TR-4). In order: the rule's static
 *   explanation from the catalog when `rule_id` is set (FR-009, ANA.6: never the model), the text with
 *   its line breaks, the SAT.7 band (`SAT7_VALUES` from `mandatory-copy.ts`, verbatim, never
 *   dismissible) when `sat7` (FR-008), then the sources.
 * - **Sources** (research §1.10). The SPA builds every href: a static explanation links to
 *   `/app/rules?rule=<id>` when its ref is a rule id; the CRRG section and the layout spec are caption
 *   text (22/4 deviations). No `dangerouslySetInnerHTML`; nothing from the API becomes an href as is.
 */
import { Link } from 'react-router-dom'

import { DisclaimerBand } from '@/components/trellis/DisclaimerBand'
import { RuleExplanation } from '@/components/trellis/RuleExplanation'
import { validRuleId } from '@/lib/api'
import type { AssistantMessage as Message, AssistantSource } from '@/lib/api/assistant'
import { SAT7_VALUES } from '@/lib/mandatory-copy'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

import { AssistantFeedback } from './AssistantFeedback'

/** `/app/rules?rule=<id>` for a ref that is a rule id, else null (research §1.10). */
function catalogHref(ref: string): string | null {
  try {
    return `/app/rules?${new URLSearchParams({ rule: validRuleId(ref) }).toString()}`
  } catch {
    return null
  }
}

function Source({ source }: { source: AssistantSource }) {
  if (source.kind === 'static_explanation') {
    const label = PROVISIONAL_COPY.assistantSourceStatic.render({ ruleId: source.ref })
    const href = catalogHref(source.ref)
    if (href === null) return <>{label}</>
    return (
      <Link to={href} className="text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
        {label}
      </Link>
    )
  }
  if (source.kind === 'crrg') return <>{PROVISIONAL_COPY.assistantSourceCrrg.render({ ref: source.ref })}</>
  return <>{PROVISIONAL_COPY.assistantSourceLayout.render({ column: source.ref })}</>
}

export function AssistantMessage({ message }: { message: Message }) {
  return (
    <li className="flex flex-col gap-3">
      <div
        data-block="question"
        className="ml-auto max-w-[85%] rounded-[12px_12px_4px_12px] bg-[var(--color-accent-50)] px-3 py-2"
      >
        <span className="sr-only">{copyText('assistantYou')}</span>
        <p className="whitespace-pre-line text-body text-navy-950">{message.question}</p>
      </div>
      {message.masked_count > 0 ? (
        <p className="ml-auto max-w-[85%] text-caption text-navy-600">
          {PROVISIONAL_COPY.assistantMaskedNotice.render({ count: message.masked_count })}
        </p>
      ) : null}
      <div data-assistant-answer="" className="flex flex-col gap-4 border-l border-navy-200 pl-4">
        {message.rule_id !== null ? (
          <div data-block="static">
            <RuleExplanation ruleId={message.rule_id} />
          </div>
        ) : null}
        <p data-block="text" className="whitespace-pre-line text-body text-navy-950">
          {message.answer}
        </p>
        {message.sat7 ? (
          <div data-block="sat7">
            <DisclaimerBand variant="caption">{SAT7_VALUES}</DisclaimerBand>
          </div>
        ) : null}
        {message.sources.length > 0 ? (
          <div data-block="sources" className="flex flex-col gap-1">
            <p className="text-caption font-medium text-navy-600">{copyText('assistantSourcesLabel')}</p>
            <ol className="flex flex-col gap-0.5 text-caption text-navy-600">
              {message.sources.map((source) => (
                <li key={`${source.kind}:${source.ref}`}>
                  <Source source={source} />
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>
      <AssistantFeedback runId={message.run_id} messageId={message.id} verdict={message.feedback} />
    </li>
  )
}
